import { useState, type FormEvent } from 'react';
import { errorMessage, useLocale, type MessageKey } from '../../i18n';
import { Field, StatusMessage } from '../ui';
import {
  searchAudit as defaultSearch,
  type AuditEventRow,
  type AuditFilters,
  type AuditPage,
  type AuditSearchResult,
} from '../../lib/api/platformAudit';

// ─────────────────────────────────────────────────────────────────────
// /sa → Revisjon (#1041). A READ-ONLY view of the audit trail.
//
// «Who changed this club's settings?» and «what did support do while acting
// as this club?» needed a developer with a database connection. This is the
// place they are answered in the product.
//
// WHAT IT DOES NOT DO: decide anything. The server validates every filter,
// decides who may read (`platform.audit.read`, Entra only), and records each
// search as an `audit.read` row. This sends the filters and renders the rows.
//
// NO SEARCH UNTIL ASKED. Every search is recorded, so opening the section
// must not be one: the reader presses «Søk», and the lead says it is logged.
//
// `detail` IS SHOWN AS STORED. Whether some of it should be masked for some
// readers is #935's decision, not this screen's.
//
// FROM AN IMPERSONATION CONTEXT, ONE CLICK. A row that carries a context id
// offers «everything done under it»: the #934 trail (`impersonation.request`)
// and each route's own rows, which carry the same id. It replaces the
// filters with exactly that one, so what is listed is the whole context and
// nothing else.
// ─────────────────────────────────────────────────────────────────────

export interface AuditSectionProps {
  /** Injectable for tests; defaults to the real client. */
  search?: (filters: AuditFilters, cursor?: string | null) => Promise<AuditSearchResult>;
}

/** The form's fields — strings, as inputs hold them. */
interface FormState {
  organization: string;
  actor: string;
  action: string;
  /** `datetime-local` values, in the reader's own time zone. */
  from: string;
  to: string;
  impersonation: string;
  subject: string;
  subjectType: string;
}

const EMPTY: FormState = {
  organization: '',
  actor: '',
  action: '',
  from: '',
  to: '',
  impersonation: '',
  subject: '',
  subjectType: '',
};

type FilterField = keyof FormState;

/**
 * A `datetime-local` value is the reader's LOCAL time with no offset; the
 * server wants an instant. `new Date('YYYY-MM-DDTHH:mm')` reads it as local
 * time, and the instant is sent with its offset (Z). This is a timestamp for a
 * query, never a storage key — DOMAIN_RULES §4 is about date KEYS.
 */
function localToInstant(value: string): string | undefined | null {
  if (value.trim() === '') return undefined;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

const ACTOR_TYPE_KEYS: Record<string, MessageKey> = {
  MEMBER: 'saAudit.actorType.MEMBER',
  CLUB_USER: 'saAudit.actorType.CLUB_USER',
  DEVICE: 'saAudit.actorType.DEVICE',
  PLATFORM: 'saAudit.actorType.PLATFORM',
  OPERATOR: 'saAudit.actorType.OPERATOR',
  PROVIDER: 'saAudit.actorType.PROVIDER',
  SYSTEM: 'saAudit.actorType.SYSTEM',
};

const FIELD_KEYS: Record<FilterField, MessageKey> = {
  organization: 'saAudit.filter.organization',
  actor: 'saAudit.filter.actor',
  action: 'saAudit.filter.action',
  from: 'saAudit.filter.from',
  to: 'saAudit.filter.to',
  impersonation: 'saAudit.filter.impersonation',
  subject: 'saAudit.filter.subject',
  subjectType: 'saAudit.filter.subjectType',
};

type Outcome =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; filters: AuditFilters; page: AuditPage }
  | { kind: 'failed'; text: string; field: FilterField | null };

export function AuditSection({ search = defaultSearch }: AuditSectionProps) {
  const { t, formatDate } = useLocale();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState<string | null>(null);

  const set = (field: FilterField) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const refusal = (result: Exclude<AuditSearchResult, { state: 'ok' }>): Outcome => {
    if (result.state === 'no-api') {
      return { kind: 'failed', text: t('saAudit.error.noApi'), field: null };
    }
    if (result.state === 'error') {
      return { kind: 'failed', text: t('saAudit.error.failed'), field: null };
    }
    if (result.code === 'invalid_filter' || result.code === 'unknown_filter') {
      const field =
        result.field && result.field in FIELD_KEYS ? (result.field as FilterField) : null;
      return { kind: 'failed', text: t('saAudit.error.invalidFilter'), field };
    }
    if (result.code === 'forbidden') {
      return { kind: 'failed', text: t('saAudit.error.forbidden'), field: null };
    }
    if (result.code === 'entra_required') {
      return { kind: 'failed', text: t('saAudit.error.entraRequired'), field: null };
    }
    return { kind: 'failed', text: errorMessage(t, result.code), field: null };
  };

  const run = async (filters: AuditFilters) => {
    setOutcome({ kind: 'loading' });
    setMoreFailed(null);
    const result = await search(filters);
    setOutcome(
      result.state === 'ok' ? { kind: 'ok', filters, page: result.page } : refusal(result),
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const from = localToInstant(form.from);
    const to = localToInstant(form.to);
    if (from === null || to === null) {
      setOutcome({
        kind: 'failed',
        text: t('saAudit.error.invalidFilter'),
        field: from === null ? 'from' : 'to',
      });
      return;
    }
    void run({
      organization: form.organization,
      actor: form.actor,
      action: form.action,
      from,
      to,
      impersonation: form.impersonation,
      subject: form.subject,
      subjectType: form.subjectType,
    });
  };

  const clear = () => {
    setForm(EMPTY);
    setOutcome({ kind: 'idle' });
    setMoreFailed(null);
  };

  /** Everything done under one impersonation context — the whole trail, nothing else. */
  const showContext = (impersonationId: string) => {
    setForm({ ...EMPTY, impersonation: impersonationId });
    void run({ impersonation: impersonationId });
  };

  const loadMore = async () => {
    if (outcome.kind !== 'ok' || !outcome.page.nextCursor) return;
    const current = outcome;
    setLoadingMore(true);
    setMoreFailed(null);
    const result = await search(current.filters, current.page.nextCursor);
    setLoadingMore(false);
    if (result.state !== 'ok') {
      const failed = refusal(result);
      setMoreFailed(failed.kind === 'failed' ? failed.text : t('saAudit.error.failed'));
      return;
    }
    setOutcome({
      kind: 'ok',
      filters: current.filters,
      page: {
        events: [...current.page.events, ...result.page.events],
        nextCursor: result.page.nextCursor,
        accounts: { ...current.page.accounts, ...result.page.accounts },
        organizations: { ...current.page.organizations, ...result.page.organizations },
      },
    });
  };

  const errorFor = (field: FilterField) =>
    outcome.kind === 'failed' && outcome.field === field ? outcome.text : undefined;

  const textField = (field: FilterField, hint?: MessageKey) => (
    <Field label={t(FIELD_KEYS[field])} hint={hint ? t(hint) : undefined} error={errorFor(field)}>
      <input
        type="text"
        className="input w-full text-xs font-mono"
        value={form[field]}
        onChange={(e) => set(field)(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        data-testid={`sa-audit-filter-${field}`}
      />
    </Field>
  );

  const page = outcome.kind === 'ok' ? outcome.page : null;

  const accountName = (id: string | null) =>
    id ? (page?.accounts[id]?.displayName ?? null) : null;
  const organizationName = (id: string | null) =>
    id ? (page?.organizations[id]?.name ?? null) : null;

  return (
    <section className="card p-6" data-testid="sa-audit">
      <h1 className="font-display text-2xl font-semibold text-ink">{t('saAudit.title')}</h1>
      <p className="text-sm text-ink-3 mt-1">{t('saAudit.lead')}</p>

      <form className="mt-5" onSubmit={submit} data-testid="sa-audit-form" noValidate>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {textField('organization', 'saAudit.hint.id')}
          {textField('actor', 'saAudit.hint.actor')}
          {textField('action', 'saAudit.hint.action')}
          {textField('impersonation', 'saAudit.hint.id')}
          <Field label={t(FIELD_KEYS.from)} error={errorFor('from')}>
            <input
              type="datetime-local"
              className="input w-full text-xs"
              value={form.from}
              onChange={(e) => set('from')(e.target.value)}
              data-testid="sa-audit-filter-from"
            />
          </Field>
          <Field label={t(FIELD_KEYS.to)} hint={t('saAudit.hint.to')} error={errorFor('to')}>
            <input
              type="datetime-local"
              className="input w-full text-xs"
              value={form.to}
              onChange={(e) => set('to')(e.target.value)}
              data-testid="sa-audit-filter-to"
            />
          </Field>
          {textField('subject')}
          {textField('subjectType', 'saAudit.hint.subjectType')}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            className="btn-primary text-xs"
            disabled={outcome.kind === 'loading'}
            data-testid="sa-audit-search"
          >
            {outcome.kind === 'loading' ? t('saAudit.searching') : t('saAudit.search')}
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={clear}
            data-testid="sa-audit-clear"
          >
            {t('saAudit.clear')}
          </button>
          <span className="text-[11px] text-ink-3">{t('saAudit.logged')}</span>
        </div>
      </form>

      <div className="mt-6" aria-live="polite">
        {outcome.kind === 'idle' && (
          <p className="text-[11px] text-ink-3" data-testid="sa-audit-idle">
            {t('saAudit.idle')}
          </p>
        )}
        {outcome.kind === 'failed' && (
          <div data-testid="sa-audit-failed">
            <StatusMessage kind="error">{outcome.text}</StatusMessage>
          </div>
        )}
        {page && page.events.length === 0 && (
          <p className="text-[11px] text-ink-3" data-testid="sa-audit-empty">
            {t('saAudit.empty')}
          </p>
        )}
      </div>

      {page && page.events.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] text-ink-3" data-testid="sa-audit-count">
            {t('saAudit.count', { count: String(page.events.length) })}
          </p>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid="sa-audit-results">
              <caption className="sr-only">{t('saAudit.table.caption')}</caption>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.when')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.who')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.what')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.organization')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.assurance')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('saAudit.col.impersonation')}
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    {t('saAudit.col.detail')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.events.map((row: AuditEventRow) => {
                  const who = accountName(row.actorId);
                  const org = organizationName(row.organizationId);
                  const subjectName = accountName(row.subjectId) ?? organizationName(row.subjectId);
                  const actorTypeKey = ACTOR_TYPE_KEYS[row.actorType];
                  return (
                    <tr
                      key={row.id}
                      className="border-t border-white/10 align-top"
                      data-testid={`sa-audit-row-${row.id}`}
                    >
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        <time dateTime={row.at}>
                          {formatDate(new Date(row.at), {
                            dateStyle: 'short',
                            timeStyle: 'medium',
                          })}
                        </time>
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className="block">{who ?? row.actorId ?? t('saAudit.none')}</span>
                        <span className="block text-[10px] text-ink-3">
                          {actorTypeKey ? t(actorTypeKey) : row.actorType}
                        </span>
                        {who && row.actorId && (
                          <span className="block font-mono text-[10px] text-ink-3 break-all">
                            {row.actorId}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className="block font-mono">{row.action}</span>
                        {row.subjectId && (
                          <span className="block text-[10px] text-ink-3 break-all">
                            {t('saAudit.subject', {
                              type: row.subjectType ?? t('saAudit.none'),
                              id: subjectName ? `${subjectName} (${row.subjectId})` : row.subjectId,
                            })}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">
                        {row.organizationId ? (
                          <>
                            <span className="block">{org ?? row.organizationId}</span>
                            {org && (
                              <span className="block font-mono text-[10px] text-ink-3 break-all">
                                {row.organizationId}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-ink-3">{t('saAudit.platformWide')}</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3 font-mono">
                        {row.assurance ?? <span className="text-ink-3">{t('saAudit.none')}</span>}
                      </td>
                      <td className="py-1.5 pr-3">
                        {row.impersonationId ? (
                          <>
                            <span className="block font-mono text-[10px] break-all">
                              {row.impersonationId}
                            </span>
                            <button
                              type="button"
                              className="btn-ghost text-[11px] mt-1"
                              onClick={() => showContext(row.impersonationId!)}
                              aria-label={t('saAudit.showContext.label', {
                                id: row.impersonationId,
                              })}
                              data-testid={`sa-audit-context-${row.id}`}
                            >
                              {t('saAudit.showContext')}
                            </button>
                          </>
                        ) : (
                          <span className="text-ink-3">{t('saAudit.none')}</span>
                        )}
                      </td>
                      <td className="py-1.5">
                        {row.detail === null || row.detail === undefined ? (
                          <span className="text-ink-3">{t('saAudit.none')}</span>
                        ) : (
                          <pre className="font-mono text-[10px] whitespace-pre-wrap break-all max-w-md">
                            {JSON.stringify(row.detail)}
                          </pre>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {page.nextCursor && (
            <button
              type="button"
              className="btn-ghost text-xs mt-3"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              data-testid="sa-audit-more"
            >
              {loadingMore ? t('saAudit.searching') : t('saAudit.more')}
            </button>
          )}
          {moreFailed && (
            <div className="mt-2" data-testid="sa-audit-more-failed">
              <StatusMessage kind="error">{moreFailed}</StatusMessage>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
